import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Sparkles, Wand2, Eye, Zap, Star } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";

interface ReprogrammePhaseProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  eventTitle: string;
}

export const ReprogrammePhase: React.FC<ReprogrammePhaseProps> = ({ isOpen, onClose, onSave, eventTitle }) => {
  const [activeTab, setActiveTab] = useState('beliefs');
  const [responses, setResponses] = useState({
    // New Core Beliefs
    newBeliefSelf: '',
    newBeliefOthers: '',
    newBeliefWorld: '',
    evidenceForNew: '',
    
    // Future Self Integration
    futureSelfMessage: '',
    wisdomGained: '',
    strengthsDiscovered: '',
    
    // Neuroplasticity Work
    affirmations: [] as string[],
    visualisationScript: '',
    newBehaviours: '',
    
    // Meaning Reconstruction
    newMeaning: '',
    postTraumaticGrowth: '',
    giftInDisguise: '',
    
    // Integration
    dailyPractice: '',
    triggerResponse: '',
    identityShift: '',
  });

  const [currentAffirmation, setCurrentAffirmation] = useState('');

  const addAffirmation = () => {
    if (currentAffirmation.trim()) {
      setResponses({
        ...responses,
        affirmations: [...responses.affirmations, currentAffirmation.trim()]
      });
      setCurrentAffirmation('');
    }
  };

  const removeAffirmation = (index: number) => {
    setResponses({
      ...responses,
      affirmations: responses.affirmations.filter((_, i) => i !== index)
    });
  };

  const handleSave = () => {
    onSave({
      reprogramme_notes: JSON.stringify(responses),
      reprogramme_completed: true
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="h-6 w-6 text-primary" />
            Re-programme Phase: {eventTitle}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Neuroplasticity-based re-programming to install new empowering beliefs
          </p>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="beliefs">New Beliefs</TabsTrigger>
            <TabsTrigger value="future">Future Self</TabsTrigger>
            <TabsTrigger value="neural">Neural Rewiring</TabsTrigger>
            <TabsTrigger value="integration">Integration</TabsTrigger>
          </TabsList>

          <TabsContent value="beliefs" className="space-y-6 mt-6">
            <Card className="p-4 bg-primary/5 border-primary/20">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-primary" />
                Reconstructing Core Beliefs
              </h3>
              <p className="text-xs text-muted-foreground">
                Replace limiting beliefs with empowering truths based on your current wisdom
              </p>
            </Card>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">New Belief About Self</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What's the TRUTH about who you are? Write in present tense as if already true.
                </p>
                <Textarea
                  value={responses.newBeliefSelf}
                  onChange={(e) => setResponses({ ...responses, newBeliefSelf: e.target.value })}
                  rows={4}
                  placeholder="I am inherently worthy. I am enough exactly as I am. I am resilient, wise, and capable. My past does not define my worth..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">New Belief About Others</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What's true about healthy relationships, trust, and connection?
                </p>
                <Textarea
                  value={responses.newBeliefOthers}
                  onChange={(e) => setResponses({ ...responses, newBeliefOthers: e.target.value })}
                  rows={4}
                  placeholder="There are people who can hold my wholeness. I am worthy of authentic, reciprocal love. Vulnerability is strength, not weakness..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">New Belief About the World</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What empowering worldview are you choosing?
                </p>
                <Textarea
                  value={responses.newBeliefWorld}
                  onChange={(e) => setResponses({ ...responses, newBeliefWorld: e.target.value })}
                  rows={4}
                  placeholder="The universe is abundant. I am supported. Life happens FOR me, not TO me. There is enough love, success, and belonging for everyone..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Evidence Supporting New Beliefs</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Your brain needs proof. What evidence do you have that these new beliefs are true?
                </p>
                <Textarea
                  value={responses.evidenceForNew}
                  onChange={(e) => setResponses({ ...responses, evidenceForNew: e.target.value })}
                  rows={5}
                  placeholder="Times I've been loved for being authentic. Moments of unexpected support. Evidence of my resilience. Examples of abundance appearing..."
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="future" className="space-y-6 mt-6">
            <Card className="p-4 bg-primary/5 border-primary/20">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Eye className="h-4 w-4 text-primary" />
                Future Self Integration
              </h3>
              <p className="text-xs text-muted-foreground">
                Connect with your healed future self who's already integrated this
              </p>
            </Card>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Message from Your Future Self</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Close your eyes. Your future self who's healed this completely - what do they want you to know?
                </p>
                <Textarea
                  value={responses.futureSelfMessage}
                  onChange={(e) => setResponses({ ...responses, futureSelfMessage: e.target.value })}
                  rows={5}
                  placeholder="Dear Past Me, I'm so proud of you for doing this work. I want you to know that you do heal. This becomes your superpower. You help others because you walked through this fire. You're becoming me right now..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Wisdom Extracted from the Pain</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What wisdom, strength, or gift did you gain from surviving this?
                </p>
                <Textarea
                  value={responses.wisdomGained}
                  onChange={(e) => setResponses({ ...responses, wisdomGained: e.target.value })}
                  rows={4}
                  placeholder="I learned compassion for others' pain. I developed deep empathy. I know what true strength looks like. I understand nuance and complexity..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Strengths Forged in Fire</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What personal strengths did you develop as a result of this experience?
                </p>
                <Textarea
                  value={responses.strengthsDiscovered}
                  onChange={(e) => setResponses({ ...responses, strengthsDiscovered: e.target.value })}
                  rows={4}
                  placeholder="Resilience. Emotional intelligence. Intuition. Boundaries. Discernment. The ability to sit with discomfort. Courage. Self-awareness..."
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="neural" className="space-y-6 mt-6">
            <Card className="p-4 bg-primary/5 border-primary/20">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary" />
                Neuroplasticity Protocols
              </h3>
              <p className="text-xs text-muted-foreground">
                Rewire your neural pathways through repetition, visualisation, and new behaviours
              </p>
            </Card>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Power Affirmations (3-5 core statements)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Short, powerful, present-tense statements you'll repeat daily to rewire beliefs
                </p>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={currentAffirmation}
                    onChange={(e) => setCurrentAffirmation(e.target.value)}
                    placeholder="I am worthy of deep love and belonging"
                    onKeyPress={(e) => e.key === 'Enter' && addAffirmation()}
                  />
                  <Button onClick={addAffirmation} type="button">Add</Button>
                </div>
                <div className="space-y-2">
                  {responses.affirmations.map((affirmation, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                      <span className="text-sm">{affirmation}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAffirmation(index)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Visualisation Script</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Write a detailed scene of you embodying these new beliefs. Use all 5 senses. Visualise daily.
                </p>
                <Textarea
                  value={responses.visualisationScript}
                  onChange={(e) => setResponses({ ...responses, visualisationScript: e.target.value })}
                  rows={6}
                  placeholder="I see myself walking into a room, shoulders back, fully present. I feel grounded and worthy. When someone criticises me, I remain centred. I hear my inner voice respond with compassion. I feel the freedom in my body..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">New Behavioural Patterns</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What specific actions will you take that align with your new beliefs? Be concrete.
                </p>
                <Textarea
                  value={responses.newBehaviours}
                  onChange={(e) => setResponses({ ...responses, newBehaviours: e.target.value })}
                  rows={5}
                  placeholder="I will speak up when I disagree instead of people-pleasing. I'll take one risk per week. I'll practise receiving compliments without deflecting. I'll set boundaries when my body says no..."
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="integration" className="space-y-6 mt-6">
            <Card className="p-4 bg-primary/5 border-primary/20">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Star className="h-4 w-4 text-primary" />
                Integration & Daily Practice
              </h3>
              <p className="text-xs text-muted-foreground">
                Make this transformation permanent through consistent practice
              </p>
            </Card>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Daily Integration Practice</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What will you do EVERY DAY to reinforce these new neural pathways?
                </p>
                <Textarea
                  value={responses.dailyPractice}
                  onChange={(e) => setResponses({ ...responses, dailyPractice: e.target.value })}
                  rows={5}
                  placeholder="Morning: Read affirmations 3x with feeling. Visualise for 5 minutes. Evening: Journal one example of living from new belief. Weekly: Intentionally practice one new behaviour..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">New Trigger Response Protocol</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  When the old trigger happens, what's your new empowered response?
                </p>
                <Textarea
                  value={responses.triggerResponse}
                  onChange={(e) => setResponses({ ...responses, triggerResponse: e.target.value })}
                  rows={5}
                  placeholder="When I feel criticized: PAUSE. Notice the body sensation. Remind myself 'This is old wiring, not current truth.' Breathe. Respond from my adult self, not wounded child. Choose a behaviour that aligns with my new belief..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Identity Shift Declaration</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Who are you becoming? Declare your new identity with conviction.
                </p>
                <Textarea
                  value={responses.identityShift}
                  onChange={(e) => setResponses({ ...responses, identityShift: e.target.value })}
                  rows={5}
                  placeholder="I am no longer someone who shrinks to make others comfortable. I am a person who trusts their intuition, speaks their truth, and takes up space unapologetically. I am becoming the version of me who is free..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Post-Traumatic Growth Recognition</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  How has this experience ultimately made you MORE?
                </p>
                <Textarea
                  value={responses.postTraumaticGrowth}
                  onChange={(e) => setResponses({ ...responses, postTraumaticGrowth: e.target.value })}
                  rows={4}
                  placeholder="This pain cracked me open. It deepened my capacity for joy, connection, and meaning. It showed me my strength. It connected me to my purpose. It made me MORE human, MORE compassionate, MORE alive..."
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Upgrade Prompt for Future Self Navigator */}
        <Card className="mt-6 p-6 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-primary/30">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
              <Star className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Ready to Go Deeper? Unlock Future Self Navigator
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Take your transformation to the next level with our comprehensive Future Self Navigator & Manifestation Package. 
                Includes guided timeline creation, quantum journaling, vision board tools, manifestation templates, and personalised 
                future self integration exercises designed to accelerate your healing journey.
              </p>
              <Button variant="default" className="gap-2">
                <Sparkles className="h-4 w-4" />
                Learn More & Upgrade
              </Button>
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Complete Re-programming
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};